/**
 * Value formatting and type detection utilities for audit logs.
 */

import { UUID_PATTERN, USER_AGENT_FIELD_NAMES, ALL_CAPS_WORDS } from './constants';
import { isISOTimestamp, formatTimestamp, isDurationString, formatDuration } from './date-formatting';

/**
 * Check if a value is a nullable database field object.
 * These have the structure: { string: "...", valid: true/false } or { int64: ..., valid: true/false }
 *
 * @example
 * isNullableDbField({ string: "hello", valid: true }) // true
 * isNullableDbField({ int64: 123, valid: true }) // true
 * isNullableDbField({ name: "test" }) // false
 */
export const isNullableDbField = (value: unknown): value is { string?: string; int64?: number; valid: boolean } => {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const object = value as Record<string, unknown>;
  // Must have 'valid' as a boolean and either 'string' or 'int64' field
  if (typeof object.valid !== 'boolean') {
    return false;
  }
  // Check for exactly the expected keys (valid + string/int64)
  const keys = Object.keys(object);
  if (keys.length !== 2) {
    return false;
  }
  return 'string' in object || 'int64' in object;
};

/**
 * Extract the value from a nullable database field.
 * Returns null if not valid, otherwise returns the string or int64 value.
 */
export const extractNullableDbValue = (value: {
  string?: string;
  int64?: number;
  valid: boolean;
}): string | number | null => {
  if (!value.valid) {
    return null;
  }
  if ('string' in value) {
    return value.string ?? null;
  }
  if ('int64' in value) {
    return value.int64 ?? null;
  }
  return null;
};

/**
 * Check if a string is valid JSON (object or array).
 *
 * @param value - The string to check
 * @returns true if the string is valid JSON and starts with { or [
 *
 * @example
 * isJSONString('{"key": "value"}') // true
 * isJSONString('[1, 2, 3]') // true
 * isJSONString('not json') // false
 */
export const isJSONString = (value: string): boolean => {
  try {
    JSON.parse(value);
    return value.trim().startsWith('{') || value.trim().startsWith('[');
  } catch {
    return false;
  }
};

/**
 * Check if a value is redacted
 * Returns true for values like "[REDACTED]", "REDACTED", "[REDACTED:...]", or strings starting with "REDACTED" (case-insensitive)
 */
export const isRedactedValue = (value: unknown): boolean => {
  if (typeof value === 'string') {
    const normalized = value.trim().toUpperCase();
    // Check for exact match or pattern like [REDACTED:...]
    if (normalized === '[REDACTED]' || normalized.startsWith('[REDACTED')) {
      return true;
    }
    // Check for values starting with REDACTED (without brackets)
    return normalized.startsWith('REDACTED');
  }
  return false;
};

/**
 * Determine if a value should use monospace font (UUIDs, URLs, User Agents)
 * Note: Numbers and ID fields no longer get monospace styling for cleaner display
 * @param value - The value to check
 * @param fieldName - The field name for context
 * @returns true if value should be displayed in monospace
 */
export const shouldUseMonospace = (value: unknown, fieldName?: string): boolean => {
  if (typeof value === 'string') {
    const string_ = value.toLowerCase();
    const field = (fieldName ?? '').toLowerCase();

    // Check if value is a redacted value (e.g., "[REDACTED:8ce48da3]")
    if (string_.startsWith('[redacted')) {
      return true;
    }

    // Check if field name suggests it's a User Agent
    const normalizedField = field.replace(/[_-]/g, '');
    if (USER_AGENT_FIELD_NAMES.has(normalizedField)) {
      return true;
    }

    // Check if value looks like a User Agent string (starts with Mozilla/ or similar)
    if (string_.startsWith('mozilla/') || string_.includes('(compatible;') || string_.includes('applewebkit/')) {
      return true;
    }

    // Check if value looks like a UUID
    if (UUID_PATTERN.test(string_)) {
      return true;
    }

    // Check if value looks like a URL
    if (string_.startsWith('http://') || string_.startsWith('https://')) {
      return true;
    }
  }
  return false;
};

/**
 * Helper to check if an attribute name should use Code component styling
 * Note: ID fields no longer get code styling for cleaner display
 */
export const shouldUseCodeStyle = (_attributeName: string): boolean => {
  return false;
};

/**
 * Format boolean values for display
 */
export const formatBooleanValue = (value: boolean): string => {
  return value ? 'yes' : 'no';
};

/**
 * Check if a value looks like an email address
 * @param value - The value to check
 * @returns true if value appears to be an email
 */
export const isEmailValue = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  // Basic email pattern: something@something.something
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

/**
 * Check if a string value matches a known acronym (case-insensitive).
 * Returns the properly cased acronym if found, otherwise null.
 */
const getAcronymValue = (value: string): string | null => {
  const upperValue = value.toUpperCase();
  const match = ALL_CAPS_WORDS.find((acronym) => acronym.toUpperCase() === upperValue);
  return match ?? null;
};

/**
 * Format a value for display in the audit log details view.
 *
 * Transformations applied:
 * - null/undefined → "—" (em dash)
 * - empty string → "—"
 * - boolean → "yes" / "no"
 * - ISO timestamp strings → human-readable date
 * - Duration strings (e.g., "168h0m0s") → human-readable (e.g., "1 week")
 * - Permission objects → formatted permission string
 * - Acronym values (sso, api, etc.) → uppercase (SSO, API)
 * - Other values → string representation
 *
 * @param value - The raw value to format
 * @returns Formatted string for display
 *
 * @example
 * formatValue(null) // "—"
 * formatValue(true) // "yes"
 * formatValue('168h0m0s') // "1 week"
 * formatValue('2025-11-27T00:41:08Z') // "Nov 27, 2025, 12:41:08 AM"
 * formatValue('sso') // "SSO"
 */
export const formatValue = (value: unknown): string => {
  if (value == null) {
    return '—';
  }

  // Handle nullable database fields: { string: "...", valid: true/false }
  if (isNullableDbField(value)) {
    const extracted = extractNullableDbValue(value);
    if (extracted == null) {
      return '—';
    }
    // Recursively format the extracted value
    return formatValue(extracted);
  }

  // Handle empty strings
  if (typeof value === 'string' && value.length === 0) {
    return '—';
  }

  // Normalize redacted values to just [REDACTED] (remove encoded suffix)
  if (typeof value === 'string' && isRedactedValue(value)) {
    return '[REDACTED]';
  }

  // Check for ISO timestamp strings
  if (isISOTimestamp(value)) {
    return formatTimestamp(value);
  }

  // Check for Unix timestamps (numbers that look like timestamps)
  if (typeof value === 'number') {
    // Unix timestamps in seconds (10 digits) or milliseconds (13 digits)
    if (value > 1000000000 && value < 10000000000) {
      return formatTimestamp(value);
    } else if (value > 1000000000000 && value < 10000000000000) {
      return formatTimestamp(value);
    }
    return String(value);
  }

  if (typeof value === 'boolean') {
    return formatBooleanValue(value);
  }

  // Check for duration strings (e.g., "168h0m0s" -> "1 week")
  if (typeof value === 'string' && isDurationString(value)) {
    return formatDuration(value);
  }

  // Check if string value is a known acronym (sso -> SSO, api -> API)
  if (typeof value === 'string') {
    const acronym = getAcronymValue(value);
    if (acronym != null) {
      return acronym;
    }
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};
